import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useDispatch } from 'react-redux'

import useInfiniteData from 'hooks/useInfiniteData'
import { THistoryGoals } from '../../Dashboard/Providers/types'
import { setLoading } from '../../../redux/store/common/slice'
import { TQueryParams } from '../../../components/Table/types'
import { useSavingGoalsPopup } from './SavingGoalsPopupProvider'
import {
  SelectItemToShowHistoryFunc,
  SelectItemToShowHistoryItem
} from '../types'
import {
  getHistorySavingsCommunity,
  getHistorySavingsHealthSystem,
  getHistorySavingsHospital
} from '../api'
import { useSavingGoalsContext } from './SavingGolasContextProvider'

type ContextProps = {
  state: {
    hasNextPage: boolean
    history: THistoryGoals
    total: number
    selectedItem: SelectItemToShowHistoryItem | null
  }
  actions: {
    handleLoadMore: VoidFunction
    selectItemToShowHistory: SelectItemToShowHistoryFunc
  }
}

const SavingGoalsHistoryContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const SavingGoalsHistoryContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const dispatch = useDispatch()

  const { historyGoalsPopup } = useSavingGoalsPopup()
  const {
    state: { period }
  } = useSavingGoalsContext()
  const [total, setTotal] = useState(0)
  const [selectedItem, setSelectedItem] =
    useState<SelectItemToShowHistoryItem | null>(null)

  const [history, setHistory] = useState<THistoryGoals>([])

  const formattedPeriod = useMemo(() => period.format('YYYY'), [period])

  const selectItemToShowHistory: SelectItemToShowHistoryFunc = useCallback(
    (item) => {
      setSelectedItem(item)
      historyGoalsPopup.actions.open()
    },
    [historyGoalsPopup]
  )

  const getHistorySavings = useMemo(() => {
    switch (selectedItem?.type) {
      case 'community':
        return getHistorySavingsCommunity
      case 'hospital':
        return getHistorySavingsHospital
      default:
        return getHistorySavingsHealthSystem
    }
  }, [selectedItem?.type])

  const loadMore = useCallback(
    async (args: TQueryParams) => {
      if (!selectedItem) {
        throw new Error('Item is not provided')
      }

      const resp = await getHistorySavings(selectedItem.uuid, {
        params: { ...args, year: formattedPeriod }
      })
      if (resp.data?.results) {
        setHistory((prev) => [...prev, ...resp.data.results])
      }
    },
    [selectedItem, formattedPeriod, getHistorySavings]
  )
  const { limit, hasNextPage, handleLoadMore } = useInfiniteData({
    total,
    limit: 50,
    loadMore
  })
  const handleGetHistory = useCallback(async () => {
    dispatch(setLoading(true))
    if (!selectedItem) {
      throw new Error('Item is not provided')
    }
    try {
      const resp = await getHistorySavings(selectedItem.uuid, {
        params: {
          year: formattedPeriod,
          limit: limit || 0
        }
      })
      if (resp.data?.results) {
        setHistory(resp.data.results)
        setTotal(resp.data.count)
      }
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedItem, formattedPeriod])

  useEffect(() => {
    if (selectedItem && historyGoalsPopup.state.visible) handleGetHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, historyGoalsPopup, handleGetHistory])

  const context = useMemo(
    () => ({
      state: {
        history,
        hasNextPage,
        total,
        selectedItem
      },
      actions: {
        handleLoadMore,
        selectItemToShowHistory
      }
    }),
    [
      history,
      hasNextPage,
      handleLoadMore,
      total,
      selectItemToShowHistory,
      selectedItem
    ]
  )

  return (
    <SavingGoalsHistoryContext.Provider value={context}>
      {children}
    </SavingGoalsHistoryContext.Provider>
  )
}

export const useSavingGoalsHistoryContext = () =>
  useContext(SavingGoalsHistoryContext)

export default SavingGoalsHistoryContextProvider
